import { useEffect, useRef, useState } from "react"
import { Button, Dropdown, Flex, Form } from "antd"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Card } from "ui/Card"
import { Icon } from "ui/Icon"
import { InfoBox } from "ui/InfoBox"
import { Text } from "ui/Typography"
import { spacings } from "themes/variables"
import { PricingTypeSelector } from "pagesV2/pro/Experiences/ListingScheduling/LengthAndRates/PricingTypeSelector"
import { DeleteConfirmation } from "pagesV2/pro/DigitalWaiverAndContracts/DocumentsAdded/Deleteconfirmation"
import { AddBookingOptionModal } from "../AddBookingOptionModal/AddBookingOptionModal"
import { PRICING_TYPES, TRIP_PRICE } from "pagesV2/pro/Experiences/ListingScheduling/utils/constants"
import { isServerId } from "../../../mappers/toApiPayload"
import { useWizardContext } from "../../../WizardContext"
import { durationFormatter } from "utils/durationFormatter"
import { numberToCurrency } from "helpers/string"
import styles from "./MultiDayFixedPricingModel.module.css"

// "2 days" / "1 day"
function formatDurationLabel(days, t) {
  const key = days === 1 ? "day" : "days"
  return t(`experiences.pricing.private.option_row.${key}`, { days })
}

// "2 days • Per person" / "2 days • Flat rate • $2,000.00"
function buildDayRowTitle(record, fallbackPricingType, t) {
  const isFlatRate = (record.pricing_type || fallbackPricingType) === PRICING_TYPES.FLAT_RATE
  const typeLabel = t(`experiences.pricing.private.option_row.${isFlatRate ? "flat_rate" : "per_person"}`)
  const days = record[TRIP_PRICE.DURATION]
  const parts = [days ? formatDurationLabel(days, t) : null, typeLabel]
  if (isFlatRate && record[TRIP_PRICE.FLAT_RATE] != null) {
    parts.push(numberToCurrency(record[TRIP_PRICE.FLAT_RATE]))
  }
  return parts.filter(Boolean).join(" • ")
}

// Adults are always allowed; the remaining types are opt-in per booking option.
const GUEST_TYPES = [
  { allowed: TRIP_PRICE.ADULTS_ALLOWED, price: TRIP_PRICE.PRICE_ADULT, label: "adults", isAlwaysAllowed: true },
  { allowed: TRIP_PRICE.CHILDREN_ALLOWED, price: TRIP_PRICE.PRICE_CHILD, label: "children" },
  { allowed: TRIP_PRICE.INFANTS_ALLOWED, price: TRIP_PRICE.PRICE_INFANT, label: "infants" },
  { allowed: TRIP_PRICE.PASSENGERS_ALLOWED, price: TRIP_PRICE.PRICE_PASSENGER, label: "passengers" },
]

const guestTypeLabel = (label, t) => t(`experiences.scheduling.sections.length_and_rates.${label}`)

const isGuestTypeOn = (record, { allowed, isAlwaysAllowed }) => isAlwaysAllowed || !!record[allowed]

// "8 included: Adults / Children", "Extras up to 2 guests: $250.00"
function buildFlatRateSections(record, maxGuests, t) {
  const sections = []
  const guestsIncluded = record[TRIP_PRICE.GUESTS_INCLUDED]

  if (guestsIncluded != null) {
    const types = GUEST_TYPES.filter((guestType) => isGuestTypeOn(record, guestType))
      .map(({ label }) => guestTypeLabel(label, t))
      .join(" / ")
    sections.push(t("experiences.pricing.private.option_row.included", { guests: guestsIncluded, types }))
  }

  const notIncluded =
    typeof maxGuests === "number" && typeof guestsIncluded === "number"
      ? Math.max(0, maxGuests - guestsIncluded)
      : 0
  if (notIncluded > 0 && record[TRIP_PRICE.PRICE_ADULT] != null) {
    sections.push(
      t("experiences.pricing.private.option_row.extras", {
        guests: notIncluded,
        price: numberToCurrency(record[TRIP_PRICE.PRICE_ADULT]),
      })
    )
  }
  return sections
}

// "Adults $500.00 / Children $300.00"
function buildPerPersonSections(record, t) {
  const prices = GUEST_TYPES.filter(
    (guestType) => isGuestTypeOn(record, guestType) && record[guestType.price] != null
  ).map(({ price, label }) => `${guestTypeLabel(label, t)} ${numberToCurrency(record[price])}`)
  return prices.length ? [prices.join(" / ")] : []
}

/**
 * Per person: "Adults $500.00 / Children $300.00 • Buffer 5 hrs"
 * Flat rate:  "8 included: Adults / Children • Extras up to 2 guests: $250.00 • Buffer 5 hrs"
 */
function buildDayRowSubtitle(record, fallbackPricingType, maxGuests, t) {
  const isFlatRate = (record.pricing_type || fallbackPricingType) === PRICING_TYPES.FLAT_RATE
  const sections = isFlatRate ? buildFlatRateSections(record, maxGuests, t) : buildPerPersonSections(record, t)

  const buffer = record[TRIP_PRICE.BUFFER_TIME]
  if (buffer) {
    sections.push(t("experiences.pricing.private.option_row.buffer", { duration: durationFormatter(buffer) }))
  }
  return sections.join(" • ")
}

function DayPriceRow({ record, pricingType, maxGuests, onEditPrice, onPriceDelete }) {
  const { t } = useTranslation()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const title = buildDayRowTitle(record, pricingType, t)
  const subtitle = buildDayRowSubtitle(record, pricingType, maxGuests, t)

  const confirmDelete = () => {
    if (isServerId(record.id)) {
      onPriceDelete({ id: record.id })
    } else {
      onPriceDelete({ tempId: record.tempId })
    }
    setShowDeleteConfirm(false)
  }

  const dropdownItems = [
    { key: "delete", label: t("global.delete"), danger: true, onClick: () => setShowDeleteConfirm(true) }
  ]

  return (
    <div className={styles.row}>
      <div className={styles.rowHeader}>
        <div className={styles.rowContent}>
          <div className={styles.rowTitle}>{title}</div>
          {subtitle && <div className={styles.rowSubtitle}>{subtitle}</div>}
        </div>
        <div className={styles.rowActions}>
          {/* The circular hit area is the button; the icon keeps its own size. Padding must never
              go on the <svg> itself — the global border-box reset would shrink the glyph. */}
          <button
            type="button"
            className={styles.actionButton}
            aria-label={t("global.edit")}
            onClick={() => onEditPrice(record)}
          >
            <Icon type="feather" name="Edit2" color="currentColor" width={16} height={16} />
          </button>
          <Dropdown menu={{ items: dropdownItems }} trigger={["click"]} placement="bottomRight">
            <button type="button" className={styles.actionButtonLarge} aria-label={t("global.actions")}>
              <Icon type="feather" name="MoreHorizontal" color="currentColor" width={24} height={24} />
            </button>
          </Dropdown>
        </div>
      </div>
      <DeleteConfirmation
        visible={showDeleteConfirm}
        title={t("experiences.pricing.private.delete_booking_option_title")}
        content={t("experiences.pricing.private.delete_booking_option_content")}
        confirmText={t("experiences.pricing.private.delete_booking_option_confirm")}
        cancelText={t("experiences.pricing.adjustment_modal.cancel")}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
      />
    </div>
  )
}

DayPriceRow.propTypes = {
  record: PropTypes.object.isRequired,
  pricingType: PropTypes.string,
  maxGuests: PropTypes.number,
  onEditPrice: PropTypes.func.isRequired,
  onPriceDelete: PropTypes.func.isRequired,
}

export const MultiDayFixedPricingModel = ({ form }) => {
  const { t } = useTranslation()
  const { isFormInitialized } = useWizardContext()
  const hasSeeded = useRef(false)

  const [localPrices, setLocalPrices] = useState(() => form.getFieldValue("localPrices") || [])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalRecord, setModalRecord] = useState(null)

  useEffect(() => {
    if (!isFormInitialized || hasSeeded.current) return
    setLocalPrices(form.getFieldValue("localPrices") || [])
    hasSeeded.current = true
  }, [isFormInitialized, form])

  const pricingType = Form.useWatch("pricing_type", form) || PRICING_TYPES.PER_PERSON
  const isFlatRate = pricingType === PRICING_TYPES.FLAT_RATE
  const minGuests = Form.useWatch("min_guests", form)
  const maxGuests = Form.useWatch("max_guests", form)
  const isFlatRateDisabled = !minGuests || !maxGuests

  useEffect(() => {
    if (isFlatRateDisabled && isFlatRate) {
      form.setFieldValue("pricing_type", PRICING_TYPES.PER_PERSON)
    }
  }, [isFlatRateDisabled, isFlatRate, form])

  useEffect(() => {
    form.setFieldValue("localPrices", localPrices)
    const activeDurations = localPrices
      .filter(
        (price) =>
          !price._destroy &&
          price.trip_duration != null &&
          (!price.pricing_type || !pricingType || price.pricing_type === pricingType)
      )
      .map((price) => price.trip_duration)
    form.setFieldValue("takenDurations", activeDurations)
  }, [localPrices, form, pricingType])

  const handlePriceSave = (priceData) => {
    setLocalPrices((prev) => {
      if (priceData.id && isServerId(priceData.id)) {
        return prev.map((price) => (price.id === priceData.id ? { ...price, ...priceData } : price))
      }
      if (priceData.tempId) {
        return prev.map((price) => (price.tempId === priceData.tempId ? { ...price, ...priceData } : price))
      }
      return [...prev, { tempId: Date.now(), ...priceData }]
    })
  }

  const handlePriceDelete = ({ id, tempId }) => {
    setLocalPrices((prev) => {
      if (id && isServerId(id)) {
        return prev.map((price) => (price.id === id ? { ...price, _destroy: true } : price))
      }
      return prev.filter((price) => price.tempId !== tempId)
    })
  }

  const openModal = (record = null) => {
    setModalRecord(record)
    setIsModalOpen(true)
  }

  const displayPrices = localPrices.filter(
    (price) =>
      !price._destroy && (!price.pricing_type || !pricingType || price.pricing_type === pricingType)
  )

  return (
    <Card title={t("experiences.pricing.private.pricing_model")}>
      <Flex vertical gap={spacings.XL}>
        <PricingTypeSelector
          value={pricingType}
          setPricingType={(value) => form.setFieldValue("pricing_type", value)}
          form={form}
          setIsShowErrorMessage={() => {}}
          isFlatRateDisabled={isFlatRateDisabled}
        />

        <Flex vertical gap={spacings.XS}>
          <Flex align="center" justify="space-between">
            <Text fw={500} size="l">
              {t("experiences.pricing.private.booking_options_title")}
            </Text>
            <Button color="primary" variant="solid" size="large" onClick={() => openModal(null)}>
              <Icon type="feather" name="Plus" color="currentColor" />
              {t("experiences.pricing.private.add_option")}
            </Button>
          </Flex>
          <InfoBox className={styles.hint}>{t("experiences.pricing.private.booking_options_hint")}</InfoBox>
        </Flex>

        {displayPrices.length > 0 && (
          <div className={styles.list}>
            {displayPrices.map((record) => (
              <DayPriceRow
                key={record.id ?? record.tempId}
                record={record}
                pricingType={pricingType}
                maxGuests={typeof maxGuests === "number" ? maxGuests : undefined}
                onEditPrice={openModal}
                onPriceDelete={handlePriceDelete}
              />
            ))}
          </div>
        )}
      </Flex>

      <AddBookingOptionModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        record={modalRecord}
        wizardForm={form}
        isFlatRate={isFlatRate}
        isDayBased
        onPriceSave={handlePriceSave}
      />
    </Card>
  )
}

MultiDayFixedPricingModel.propTypes = {
  form: PropTypes.object.isRequired,
}
